/**
 * SlideCanvas — renders a single slide with a theme (and optional
 * background override on the slide). Handles gradient / color / image /
 * video backgrounds plus a configurable dim overlay + backdrop blur.
 *
 * Used by all three output modes (audience/stage/confidence) so the
 * visual output stays identical — only the surrounding chrome differs.
 */
import React from 'react';
import { BookOpen } from 'lucide-react';
import { buildSlideStyle } from './backgrounds';
import {
  hasChordPro, parseChordProLine, transposeChord,
} from './chordUtils';

export default function SlideCanvas({ slide, theme, variant = 'audience', chordMode = false, transposeSemitones = 0 }) {
  if (!slide) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-slate-950 text-slate-500">
        <p>No slide loaded</p>
      </div>
    );
  }

  const { style, resolved } = buildSlideStyle(theme, slide.background);
  const isMedia = resolved.bg_type === 'image' || resolved.bg_type === 'video';
  // Font scale differs per variant so stage/confidence show more context
  const scale = variant === 'audience' ? 1 : variant === 'stage' ? 0.75 : 0.7;
  // Chord mode is a musician view — never on audience
  const useChords = chordMode && variant !== 'audience';

  return (
    <div
      data-testid={`slide-canvas-${variant}`}
      className="relative flex h-full w-full"
      style={style}
    >
      {isMedia && resolved.bg_type === 'image' && resolved.bg_value && (
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${resolved.bg_value})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: resolved.blur ? `blur(${resolved.blur}px)` : undefined,
          }}
        />
      )}
      {isMedia && resolved.bg_type === 'video' && resolved.bg_value && (
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 h-full w-full object-cover"
          style={{ filter: resolved.blur ? `blur(${resolved.blur}px)` : undefined }}
          src={resolved.bg_value}
        />
      )}
      {resolved.overlay > 0 && (
        <div
          className="absolute inset-0"
          style={{ background: `rgba(0,0,0,${resolved.overlay})` }}
        />
      )}
      <div className="relative flex h-full w-full flex-col items-center justify-center px-[8%] py-[6%]">
        <SlideBody
          slide={slide}
          accent={resolved.accent_color}
          scale={scale}
          useChords={useChords}
          transposeSemitones={transposeSemitones}
        />
      </div>
    </div>
  );
}

function SlideBody({ slide, accent, scale, useChords, transposeSemitones }) {
  const isSong = !!slide.song;
  const isScripture = !!slide.scripture;
  const isSection = slide.section_total > 1;

  // Title sizing
  const titleClass = isSong || isScripture
    ? 'font-semibold tracking-tight opacity-80'
    : 'font-bold tracking-tight';
  const titleStyle = {
    fontSize: `${(isSong || isScripture ? 1.6 : 4) * scale}rem`,
    color: accent,
    marginBottom: `${1.2 * scale}rem`,
  };

  // Detect whether the song lyrics carry ChordPro inline chords
  const chordProDetected = useChords && isSong && hasChordPro(slide.body || '');

  return (
    <div className="w-full max-w-6xl" style={{ textAlign: 'inherit' }}>
      {slide.title && (
        <div className={titleClass} style={titleStyle} data-testid="slide-title">
          {slide.title}
          {isSection && (
            <span className="ml-3 opacity-60" style={{ fontSize: '0.55em' }}>
              {slide.section_index + 1} / {slide.section_total}
            </span>
          )}
        </div>
      )}

      {isSong && chordProDetected && (
        <div data-testid="slide-lyrics-chorded" style={{ fontSize: `${3.2 * scale}rem`, lineHeight: 1.65, fontFamily: 'inherit' }}>
          {(slide.body || '').split('\n').map((line, i) => (
            <ChordedLine key={i} line={line} accent={accent} semitones={transposeSemitones} scale={scale} />
          ))}
        </div>
      )}

      {isSong && !chordProDetected && (
        <pre
          data-testid="slide-lyrics"
          className="whitespace-pre-wrap font-inherit"
          style={{
            fontSize: `${3.4 * scale}rem`,
            lineHeight: 1.35,
            fontFamily: 'inherit',
            margin: 0,
          }}
        >
          {slide.body || '(no lyrics)'}
        </pre>
      )}

      {isScripture && (
        <div data-testid="slide-scripture">
          <div
            className="mb-4 flex items-center justify-center gap-2 opacity-80"
            style={{ fontSize: `${1.4 * scale}rem`, color: accent }}
          >
            <BookOpen className="h-5 w-5" /> {slide.scripture.ref}
            {slide.scripture.translation && (
              <span className="opacity-60">
                ({slide.scripture.translation.toUpperCase()})
              </span>
            )}
          </div>
          {(slide.scripture.verses || []).map((v) => (
            <p
              key={v.verse}
              className="mb-4"
              style={{ fontSize: `${2.8 * scale}rem`, lineHeight: 1.4 }}
            >
              <sup
                className="mr-2"
                style={{ fontSize: '0.5em', color: accent }}
              >
                {v.verse}
              </sup>
              {v.text}
            </p>
          ))}
          {(slide.scripture.verses || []).length === 0 && (
            <p style={{ fontSize: `${1.5 * scale}rem`, opacity: 0.6 }}>
              Verse not found in seed dataset.
            </p>
          )}
        </div>
      )}

      {!isSong && !isScripture && slide.body && (
        <p
          data-testid="slide-body"
          className="whitespace-pre-wrap"
          style={{ fontSize: `${2.5 * scale}rem`, lineHeight: 1.4 }}
        >
          {slide.body}
        </p>
      )}

      {slide.speaker && slide.type === 'sermon' && (
        <p
          className="mt-6 opacity-80"
          style={{ fontSize: `${1.4 * scale}rem`, color: accent }}
        >
          Speaker: {slide.speaker}
        </p>
      )}

      {slide.song && (slide.song.author || slide.song.ccli_number) && (
        <div
          className="mt-6 opacity-50"
          style={{ fontSize: `${0.75 * scale}rem` }}
        >
          {slide.song.author && <span>{slide.song.author}</span>}
          {slide.song.ccli_number && (
            <span className="ml-3">CCLI #{slide.song.ccli_number}</span>
          )}
          {useChords && transposeSemitones !== 0 && (
            <span className="ml-3" style={{ color: accent }}>
              transposed {transposeSemitones > 0 ? '+' : ''}{transposeSemitones}
            </span>
          )}
        </div>
      )}
    </div>
  );
}


function ChordedLine({ line, accent, semitones, scale }) {
  if (!line || !line.trim()) {
    return <div style={{ height: `${scale * 2}rem` }} />;
  }
  const segments = parseChordProLine(line);
  return (
    <div style={{ display: 'inline-block', width: '100%' }}>
      {segments.map((seg, i) => (
        <span key={i} style={{ display: 'inline-block', verticalAlign: 'top', whiteSpace: 'pre' }}>
          <span
            style={{
              display: 'block',
              minHeight: `${1.1 * scale}rem`,
              fontSize: `${1.1 * scale}rem`,
              color: accent,
              fontWeight: 700,
              fontFamily: 'ui-monospace, "SFMono-Regular", monospace',
            }}
          >
            {seg.chord
              ? seg.chord.split(/\s+/).map((c) => transposeChord(c, semitones)).join(' ')
              : ' '}
          </span>
          <span>{seg.text}</span>
        </span>
      ))}
    </div>
  );
}
